import { motion, AnimatePresence } from 'framer-motion';
import { Target, TrendingUp, TrendingDown, ChevronDown, Users, DollarSign, Building2, Shield, GraduationCap, Leaf, Bus, HeartPulse, Home, Scale, Gauge, Droplet, Home as HomeIcon, Briefcase, AlertTriangle, BadgeAlert, Leaf as LeafIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useCampaignStore } from '../../lib/store/campaignStore';

interface GeminiResponse {
  candidates: Array<{
    content: {
      parts: Array<{
        text: string;
      }>;
    };
  }>;
}

interface AIResponse {
  competitiveness: number;
  issues: Array<{
    name: string;
    sentiment: 'positive' | 'neutral' | 'negative';
    score: number;
    trend: 'up' | 'down' | 'stable';
    summary: string;
    voterSentiment: string;
    keyDemographics: string;
    actionItems: string[];
    impact: string;
    budget: string;
  }>;
}

interface KeyIssueData {
  name: string;
  sentiment: 'positive' | 'neutral' | 'negative';
  score: number;
  trend: 'up' | 'down' | 'stable';
  icon: keyof typeof issueIcons;
  insights: {
    summary: string;
    voterSentiment: string;
    keyDemographics: string;
    actionItems: string[];
    impact: string;
    budget: string;
  };
}

const issueIcons = {
  Building2,
  Shield,
  GraduationCap,
  Leaf,
  Bus,
  HeartPulse,
  Home,
  Scale,
  Users,
  DollarSign,
  Target,
  Droplet,
  HomeIcon,
  Briefcase,
  AlertTriangle,
  BadgeAlert,
  LeafIcon
} as const;

const issueIconMapping = {
  'Water Security and Drought Mitigation': 'Droplet',
  'Affordable Housing Crisis': 'HomeIcon',
  'Economic Development and Job Creation': 'Briefcase',
  'Homelessness Crisis': 'Home',
  'Public Safety and Crime Rates': 'BadgeAlert',
  'Sustainability and Climate Change': 'LeafIcon',
  'Economic Development': 'Building2',
  'Public Safety': 'Shield',
  'Education': 'GraduationCap',
  'Environment': 'Leaf',
  'Transportation': 'Bus',
  'Healthcare': 'HeartPulse',
  'Housing': 'Home',
  'Social Justice': 'Scale',
  'Infrastructure': 'Building2',
  'Technology': 'Building2',
  'Community Services': 'Users',
  'Fiscal Management': 'DollarSign'
} as const;

const officeSpecificIssues = {
  'School Board Member': {
    categories: [
      'Education Quality',
      'Student Safety',
      'Budget Management',
      'Curriculum Development',
      'Teacher Retention',
      'Special Education',
      'Technology Integration',
      'Parent Engagement'
    ],
    defaultIcon: 'GraduationCap'
  },
  'Mayor': {
    categories: [
      'Economic Development',
      'Public Safety',
      'Infrastructure',
      'Housing Affordability',
      'Transportation',
      'Environmental Policy',
      'Community Services',
      'Budget Management'
    ],
    defaultIcon: 'Building2'
  },
  'City Council Member': {
    categories: [
      'Local Development',
      'Neighborhood Safety',
      'Zoning Regulations',
      'Community Services',
      'Traffic Management',
      'Small Business Support',
      'Parks and Recreation',
      'Public Works'
    ],
    defaultIcon: 'Building2'
  },
  'State Representative': {
    categories: [
      'State Budget',
      'Education Funding',
      'Healthcare Access',
      'Infrastructure',
      'Economic Policy',
      'Environmental Protection',
      'Public Safety',
      'Tax Policy'
    ],
    defaultIcon: 'Building2'
  }
} as const;

const getOfficeContext = (office: string) => {
  const officeType = Object.keys(officeSpecificIssues).find(type => 
    office.toLowerCase().includes(type.toLowerCase())
  ) || 'default';

  return officeSpecificIssues[officeType as keyof typeof officeSpecificIssues] || {
    categories: [
      'Community Impact',
      'Budget Management',
      'Public Service',
      'Constituent Relations'
    ],
    defaultIcon: 'Building2'
  };
};

export function KeyIssues() {
  const [expandedIssue, setExpandedIssue] = useState<string | null>(null);
  const [hoveredIssue, setHoveredIssue] = useState<string | null>(null);
  const [competitiveness, setCompetitiveness] = useState(85);
  const [issues, setIssues] = useState<KeyIssueData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const campaignData = useCampaignStore(state => state.campaignData);

  useEffect(() => {
    const getIssueInsights = async () => {
      if (!campaignData) {
        setIsLoading(false);
        return;
      }

      const locationStr = [
        campaignData.location.city,
        campaignData.location.county,
        campaignData.location.state
      ].filter(Boolean).join(", ");

      const officeContext = getOfficeContext(campaignData.office);

      try {
        const response = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${import.meta.env.VITE_GEMINI_API_KEY}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              contents: [{
                parts: [{
                  text: `As a campaign strategist specializing in ${campaignData.office} races, analyze and provide key campaign issues for:

                  Office: ${campaignData.office}
                  Location: ${locationStr}
                  Election Type: ${campaignData.electionType}
                  Election Date: ${campaignData.electionDate.toLocaleDateString()}

                  Consider these office-specific priority areas:
                  ${officeContext.categories.map(cat => `- ${cat}`).join('\n')}

                  Rules for response:
                  1. Issue names must be 1-4 words and relevant to ${campaignData.office}
                  2. Summaries must be 10-15 words, focused on local impact
                  3. Voter sentiment must include percentage and specific constituent feedback
                  4. Demographics must focus on relevant stakeholder groups (3-5)
                  5. Action items must be specific to office authority (5-8 words each)
                  6. Impact must relate to office responsibilities (10-15 words)
                  7. Budget must align with office scope: "$X.XM for specific purpose"

                  Format the response as a JSON object with these exact keys:
                  {
                    "competitiveness": number (0-100),
                    "issues": [
                      {
                        "name": string (1-4 words),
                        "sentiment": "positive" | "neutral" | "negative",
                        "score": number (0-100),
                        "trend": "up" | "down" | "stable",
                        "summary": string (10-15 words),
                        "voterSentiment": string,
                        "keyDemographics": string,
                        "actionItems": string[],
                        "impact": string,
                        "budget": string
                      }
                    ]
                  }

                  Ensure all issues and actions are within the scope of ${campaignData.office} authority.`
                }]
              }]
            })
          }
        );

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json() as GeminiResponse;
        
        if (!data.candidates?.[0]?.content?.parts?.[0]?.text) {
          throw new Error('Invalid API response structure');
        }

        const text = data.candidates[0].content.parts[0].text;
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        
        if (!jsonMatch) {
          throw new Error('No JSON found in response');
        }

        const result = JSON.parse(jsonMatch[0]) as AIResponse;
        
        // Validate required fields
        if (typeof result.competitiveness !== 'number' || !Array.isArray(result.issues)) {
          throw new Error('Invalid response format: missing required fields');
        }

        setCompetitiveness(result.competitiveness);
        
        const mappedIssues = result.issues.map(issue => ({
          name: issue.name,
          sentiment: issue.sentiment,
          score: issue.score,
          trend: issue.trend,
          icon: issueIconMapping[issue.name as keyof typeof issueIconMapping] || officeContext.defaultIcon,
          insights: {
            summary: issue.summary,
            voterSentiment: issue.voterSentiment,
            keyDemographics: issue.keyDemographics,
            actionItems: issue.actionItems,
            impact: issue.impact,
            budget: issue.budget
          }
        }));
        
        setIssues(mappedIssues);
      } catch (error) {
        console.error('Error fetching AI insights:', error);
        
        // Use office-specific fallback data
        const fallbackIssues = officeContext.categories.slice(0, 4).map((category, index) => ({
          name: category,
          sentiment: 'positive' as const,
          score: 85 - (index * 5),
          trend: 'up' as const,
          icon: officeContext.defaultIcon as keyof typeof issueIcons,
          insights: {
            summary: `Addressing ${category.toLowerCase()} priorities for ${campaignData.office}`,
            voterSentiment: `${85 - (index * 5)}% support among key constituents`,
            keyDemographics: 'Local residents, community leaders, key stakeholders',
            impact: `Direct influence on ${category.toLowerCase()} outcomes and community service`,
            budget: `$${(1.5 - (index * 0.2)).toFixed(1)}M for ${category.toLowerCase()} initiatives`,
            actionItems: [
              `Develop comprehensive ${category.toLowerCase()} plan`,
              'Engage with key stakeholders regularly',
              'Implement transparent reporting system',
              'Create community feedback channels'
            ]
          }
        }));

        setCompetitiveness(75);
        setIssues(fallbackIssues);
      } finally {
        setIsLoading(false);
      }
    };

    getIssueInsights();
  }, [campaignData]);

  const sentimentColors = {
    positive: 'text-green-500',
    neutral: 'text-yellow-500',
    negative: 'text-red-500'
  };

  const sentimentBgColors = {
    positive: 'bg-green-500',
    neutral: 'bg-yellow-500',
    negative: 'bg-red-500'
  };

  const trendIcons = {
    up: TrendingUp,
    down: TrendingDown,
    stable: Target
  };

  const toggleIssue = (issueName: string) => {
    setExpandedIssue(expandedIssue === issueName ? null : issueName);
  };

  const locationStr = [
    campaignData?.location.city,
    campaignData?.location.county,
    campaignData?.location.state
  ].filter(Boolean).join(", ");

  if (isLoading) {
    return (
      <div className="bg-[#1a2236] rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-white/10 rounded w-1/3"></div>
          <div className="h-4 bg-white/5 rounded w-2/3"></div>
          <div className="h-4 bg-white/5 rounded-full"></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-white/5 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#1a2236] rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="space-y-2">
        <h2 className="text-xl text-gray-400">Key Campaign Issues</h2>
        <p className="text-sm text-gray-500">
          {campaignData ? `Voter sentiment analysis for ${campaignData.office} in ${locationStr}` : 'Loading analysis...'}
        </p>
      </div>

      <div className="space-y-4 mb-6 sm:mb-8">
        <div className="relative h-4 bg-white/5 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${competitiveness}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 via-yellow-500 to-red-500"
          />
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Less Competitive</span>
          <span className="font-semibold">{competitiveness}%</span>
          <span className="text-gray-400">Highly Competitive</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
          {issues.slice(0, 3).map((issue, index) => (
            <motion.div
              key={issue.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center gap-2 p-2 bg-white/5 rounded-lg"
            >
              <Target className="w-4 h-4 text-[#026fb9]" />
              <span className="text-sm line-clamp-1">{issue.name}</span>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {issues.map((issue, index) => {
          const Icon = issueIcons[issue.icon];
          const TrendIcon = trendIcons[issue.trend];
          const isExpanded = expandedIssue === issue.name;
          const isHovered = hoveredIssue === issue.name;
          
          return (
            <motion.div
              key={issue.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="overflow-hidden"
              onMouseEnter={() => setHoveredIssue(issue.name)}
              onMouseLeave={() => setHoveredIssue(null)}
            >
              <button
                onClick={() => toggleIssue(issue.name)}
                className={`w-full p-3 sm:p-4 rounded-lg transition-all duration-300 ${
                  isHovered || isExpanded ? 'bg-white/10' : 'bg-white/5'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
                  <div className="flex items-center gap-3">
                    <motion.div 
                      className={`p-2 rounded-lg ${sentimentBgColors[issue.sentiment]}/10`}
                      animate={{ scale: isHovered ? 1.1 : 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Icon className={`w-5 h-5 ${sentimentColors[issue.sentiment]}`} />
                    </motion.div>
                    <div className="font-semibold text-left line-clamp-2 sm:line-clamp-1">{issue.name}</div>
                  </div>
                  <div className="flex items-center gap-3 justify-between sm:justify-end">
                    <div className="flex items-center gap-2">
                      <TrendIcon className={`w-4 h-4 ${sentimentColors[issue.sentiment]}`} />
                      <div className={`px-3 py-1 rounded-full ${sentimentBgColors[issue.sentiment]}/10`}>
                        <span className={`text-sm font-medium ${sentimentColors[issue.sentiment]}`}>
                          {issue.score}%
                        </span>
                      </div>
                    </div>
                    <motion.div
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    </motion.div>
                  </div>
                </div>
                
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 text-left"
                    >
                      <div className="p-3 sm:p-4 bg-white/5 rounded-lg space-y-4 sm:space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <div className="text-sm text-gray-400">Summary</div>
                            <div className="font-medium line-clamp-3">{issue.insights.summary}</div>
                          </div>
                          <div className="space-y-2">
                            <div className="text-sm text-gray-400">Key Demographics</div>
                            <div className="font-medium line-clamp-2">{issue.insights.keyDemographics}</div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="p-3 bg-white/5 rounded-lg">
                            <div className="text-sm text-gray-400 mb-1">Voter Sentiment</div>
                            <div className="font-medium line-clamp-2">{issue.insights.voterSentiment}</div>
                          </div>
                          <div className="p-3 bg-white/5 rounded-lg">
                            <div className="text-sm text-gray-400 mb-1">Budget Allocation</div>
                            <div className="font-medium line-clamp-2">{issue.insights.budget}</div>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="text-sm text-gray-400">Strategic Action Items</div>
                          <div className="grid gap-2">
                            {issue.insights.actionItems.map((item, i) => (
                              <motion.div 
                                key={i}
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: i * 0.1 }}
                                className="flex items-center gap-2 p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                              >
                                <div className="w-1.5 h-1.5 rounded-full bg-[#026fb9]" />
                                <span className="text-sm line-clamp-2">{item}</span>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}